// frontend/src/components/production-sequence/SequenceTableRow.tsx
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { CommentHoverCard } from './CommentHoverCard';
import { InlineEditCell } from './InlineEditCell';
import { RecalcDeltaBadge } from './RecalcDeltaBadge';
import { fmtNum, fmtDate, COMP_BADGE, STATUS_BADGE, STATUS_BADGE_BASE } from './sequenceFormat';
import { STICKY_OFFSETS, STICKY_WIDTHS, tdWidth } from './sequenceLayout';
import { adherenceBgCls, adherenceTextCls } from '@/utils/adherencia';
import { BotonMarca, useMarcada } from '@/components/common/MarcasFilas';
import { claveFila } from '@/utils/marcasFilas';
import type { MarcasFilas } from '@/types';
import type { ProductionOrder } from '@/types/productionSequence';

interface Props {
  order: ProductionOrder;
  dark: boolean;
  /** Almacén de marcas del ámbito «secuencia», creado por la tabla. */
  marcas: MarcasFilas;
  draggable: boolean;
  selected: boolean;
  atRisk: boolean;
  onSelect: () => void;
  pending: (field: 'comentarios' | 'campoCalidad') => boolean;
  onEdit: (field: 'comentarios' | 'campoCalidad', value: string | boolean | null) => void;
}

export function SequenceTableRow({ order: o, dark, marcas, draggable, selected, atRisk, onSelect, pending, onEdit }: Props) {
  const { t } = useTranslation();
  // La OT identifica la fila en el negocio: la marca sobrevive a ordenar, a
  // filtrar por columna, a paginar y al reordenado por arrastre, que cambian el
  // índice de la fila pero nunca su OT.
  const clave = claveFila(o.id);
  const marcada = useMarcada(marcas, clave);
  // No se usa `FilaMarcable` porque este `<tr>` necesita el `ref` de dnd-kit y
  // ese componente no reenvía ref: se pinta el tinte a mano.
  const marcadoCls = marcada ? (dark ? 'fila-marcada-dark' : 'fila-marcada') : '';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: o.id, disabled: !draggable });

  const finished = o.estado === 'TERMINADO' || o.estado === 'NOTIFICADO_TERMINADO';
  const enCurso = !finished && o.udProducidas > 0 && o.udProducidas < o.udOT;
  const compNotOk = o.componentesStatus !== 'OK';

  // Fondo semántico de fila (§6.2)
  let rowBg = dark ? 'hover:bg-sky-950/30' : 'hover:bg-sky-50/60';
  if (finished) rowBg = dark ? 'bg-emerald-950/40' : 'bg-emerald-50';
  else if (enCurso) rowBg = dark ? 'bg-amber-950/40 border-l-4 border-amber-500' : 'bg-amber-50 border-l-4 border-amber-400';
  else if (compNotOk) rowBg = dark ? 'bg-slate-800/60' : 'bg-slate-100';

  // overflow-hidden + text-ellipsis: los anchos de `tdWidth` son techo, así que el
  // dato que no cabe recorta con elipsis en lugar de ensanchar la columna.
  const tdBase = `px-2 py-2 text-[12px] whitespace-nowrap overflow-hidden text-ellipsis ${dark ? 'text-slate-300' : 'text-slate-700'}`;
  // Las columnas congeladas tapan las filas al hacer scroll horizontal, así que su
  // fondo tiene que ser OPACO: no puede heredar el de la fila, que va con alpha.
  // Se replica aquí el mismo estado semántico en versión sólida para que el color de
  // la fila continúe sin corte en el bloque fijo.
  // Prioridad: selección > terminada > en curso > problema de componentes > normal.
  const stickyBg = selected ? (dark ? 'bg-sky-950' : 'bg-sky-50')
    : finished ? (dark ? 'bg-emerald-950' : 'bg-emerald-50')
    : enCurso ? (dark ? 'bg-amber-950' : 'bg-amber-50')
    : compNotOk ? (dark ? 'bg-slate-800' : 'bg-slate-100')
    : (dark ? 'bg-slate-900' : 'bg-white');
  // Las cuatro celdas congeladas llevan fondo opaco propio, que taparía el tinte
  // de la fila: cuando está marcada se les pone la clase de marca en su lugar
  // (mismo criterio que ReportTable).
  const stickyFondo = marcada ? marcadoCls : stickyBg;
  const sticky = (left: number, width: number) => ({
    position: 'sticky' as const, left, width, minWidth: width, maxWidth: width, zIndex: 1,
  });
  // Separador vertical en la primera columna de cada grupo (alineado con la cabecera).
  const dividerCls = dark ? 'border-l border-white/10' : 'border-l border-slate-200';

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Heatmap de % adherencia: verde solo dentro del objetivo 98–102 %, y rojo tanto
  // por defecto como por exceso. Umbrales en utils/adherencia.
  const adh = o.adherenciaPct;
  const adhBg = adherenceBgCls(adh, dark);
  const adhText = adherenceTextCls(adh, dark);

  return (
    <tr
      ref={setNodeRef}
      data-seq-row={o.id}
      style={style}
      onClick={() => { if (!finished) onSelect(); }}
      className={`border-t ${dark ? 'border-white/4' : 'border-slate-100'} ${selected && !isDragging ? (dark ? 'bg-sky-950/60' : 'bg-sky-50') : rowBg} ${isDragging ? 'ring-2 ring-sky-400' : selected ? 'ring-2 ring-inset ring-sky-500' : ''} ${marcadoCls} ${finished ? 'cursor-not-allowed' : 'cursor-pointer'} transition-colors`}
      title={finished ? t('sequence.recalc.finishedTooltip') : undefined}
    >
      {/* Canalón de marcado, congelado con el resto del bloque fijo. */}
      <td
        className={`px-1 py-2 align-middle ${stickyFondo}`}
        style={sticky(STICKY_OFFSETS.marca, STICKY_WIDTHS.marca)}
        title=""
      >
        <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
      </td>
      {/* ── Información general ── */}
      {/* # + handle */}
      <td className={`${tdBase} ${stickyFondo} font-mono text-center`} style={sticky(STICKY_OFFSETS.pos, STICKY_WIDTHS.pos)}>
        <span className="inline-flex items-center gap-1">
          {draggable && (
            <button {...attributes} {...listeners}
              className={`cursor-grab active:cursor-grabbing ${dark ? 'text-slate-600 hover:text-slate-300' : 'text-slate-300 hover:text-slate-600'}`}
              title="⠿" aria-label="drag">
              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor"><circle cx="3" cy="2" r="1"/><circle cx="9" cy="2" r="1"/><circle cx="3" cy="6" r="1"/><circle cx="9" cy="6" r="1"/><circle cx="3" cy="10" r="1"/><circle cx="9" cy="10" r="1"/></svg>
            </button>
          )}
          {o.sequencePosition + 1}
        </span>
      </td>
      <td className={`${tdBase} ${stickyFondo} font-mono font-semibold truncate`} style={sticky(STICKY_OFFSETS.ot, STICKY_WIDTHS.ot)} title={o.id}>{o.id}</td>
      <td className={`${tdBase} ${stickyFondo} font-semibold truncate`} style={sticky(STICKY_OFFSETS.ref, STICKY_WIDTHS.ref)} title={o.referencia}>{o.referencia}</td>
      <td className={`${tdBase} ${stickyFondo} truncate`} style={sticky(STICKY_OFFSETS.desc, STICKY_WIDTHS.desc)} title={o.descripcion}>{o.descripcion}</td>

      <td className={tdBase} style={tdWidth('familia')} title={o.familia || undefined}>{o.familia}</td>
      <td className={tdBase} style={tdWidth('subfamilia')} title={o.subfamilia || undefined}>{o.subfamilia}</td>
      <td className={tdBase} style={tdWidth('acabado')}>
        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
          {t(`sequence.acabado.${o.acabado}`)}
        </span>
      </td>
      <td className={tdBase} style={tdWidth('refNueva')}>
        {o.esReferenciaNueva
          ? <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-500/15 text-sky-500">{t('sequence.refNueva.yes')}</span>
          : <span className={dark ? 'text-slate-600' : 'text-slate-400'}>{t('sequence.refNueva.no')}</span>}
      </td>
      {/* COMENTARIOS — editable inline; se guarda en el overlay de Dataverse.
          overflow visible: el editor crece a textarea de 2 líneas y el recorte del
          texto en reposo ya lo hace el propio botón de InlineEditCell.
          title vacío: anula el tooltip nativo de la fila (ver celda de motivo). */}
      <td
        className={`${tdBase} overflow-visible`}
        style={tdWidth('comentarios')}
        title=""
        onClick={e => e.stopPropagation()}
      >
        <InlineEditCell dark={dark} value={o.comentarios} pending={pending('comentarios')} multiline onSave={v => onEdit('comentarios', v)} />
      </td>

      {/* ── Nivel de servicio ── */}
      <td className={`${tdBase} ${dividerCls}`} style={tdWidth('estado')}>
        <span className={`${STATUS_BADGE_BASE} ${STATUS_BADGE[o.estado]}`}>
          {t(`sequence.estado.${o.estado}`)}
        </span>
      </td>
      <td className={`${tdBase} text-right tabular-nums`} style={tdWidth('udOT')}>{fmtNum(o.udOT)}</td>
      <td className={`${tdBase} text-right tabular-nums`} style={tdWidth('udProd')}>{fmtNum(o.udProducidas)}</td>
      <td className={`${tdBase} text-right tabular-nums`} style={tdWidth('udPte')}>{fmtNum(o.udPendientes)}</td>
      <td className={`${tdBase} text-right ${adhBg}`} style={tdWidth('adherencia')}>
        {adh == null ? <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>
          : <span className={`tabular-nums font-semibold ${adhText}`}>{adh.toFixed(0)}%</span>}
      </td>
      <td className={tdBase} style={tdWidth('fPedido')}>{fmtDate(o.fechaPedido)}</td>
      <td
        className={`${tdBase} ${atRisk ? (dark ? 'bg-red-950/40 border-l-2 border-red-400' : 'bg-red-50 border-l-2 border-red-400') : ''}`}
        style={tdWidth('fPlanFin')}
        title={atRisk ? t('sequence.recalc.riskTooltip') : undefined}
      >
        <span className={o.recalcDelta === 0 && o.isRecalculated ? (dark ? 'text-slate-500' : 'text-slate-400') : undefined}>
          {fmtDate(o.fechaPlanificadaFin)}
        </span>
        {o.isRecalculated && o.recalcDelta != null && <RecalcDeltaBadge delta={o.recalcDelta} dark={dark} />}
      </td>
      <td className={tdBase} style={tdWidth('fRealFin')}>{fmtDate(o.fechaRealFin)}</td>
      {/* MOTIVO DESVIACIÓN — solo lectura: lo sirve la vista Fabric (ajustes y mermas
          del lote). El dato es multilínea, así que la celda muestra la primera línea y
          el texto completo lo abre CommentHoverCard (hover, foco o clic para fijar).
          title vacío: la fila lleva un tooltip nativo cuando la OT está terminada
          ("no recalculable") y tapaba la tarjeta. Un title vacío en el descendiente
          corta la búsqueda del tooltip hacia el ancestro. */}
      <td className={tdBase} style={tdWidth('motivoDesv')} title="">
        {o.motivoDesviacion
          ? <CommentHoverCard text={o.motivoDesviacion} dark={dark} />
          : <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>}
      </td>

      {/* ── Estado pre-validaciones ── */}
      <td className={`${tdBase} text-center ${dividerCls}`} style={tdWidth('calidad')} onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={o.campoCalidad === true}
          onChange={e => onEdit('campoCalidad', e.target.checked)}
          className="cursor-pointer accent-sky-500"
          aria-label={t('sequence.col.calidad')}
        />
        {pending('campoCalidad') && (
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-sky-500 align-middle" title={t('sequence.inline.pendingSync')} />
        )}
      </td>
      <td className={tdBase} style={tdWidth('granel')}>
        {o.granelStatus === 'OK'
          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-500">{t('sequence.granel.OK')} {o.granelFecha ? `· ${fmtDate(o.granelFecha)}` : ''}</span>
          : <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>{t('sequence.granel.PENDIENTE')}</span>}
      </td>
      <td className={tdBase} style={tdWidth('componentes')}>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${COMP_BADGE[o.componentesStatus]}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {t(`sequence.componentStatus.${o.componentesStatus}`)}
          {o.componentesFecha ? ` · ${fmtDate(o.componentesFecha)}` : ''}
        </span>
      </td>

      {/* ── Productividad ── */}
      <td className={`${tdBase} text-right tabular-nums ${dividerCls}`} style={tdWidth('upm')}>{fmtNum(o.upm)}</td>
      <td className={`${tdBase} text-right tabular-nums`} style={tdWidth('udTurno')}>{fmtNum(o.udTurnoPromedio)}</td>
      <td className={`${tdBase} text-right tabular-nums`} style={tdWidth('nOp')}>{fmtNum(o.nOperarios)}</td>
    </tr>
  );
}
